/**
 * PricingCard - Individual tier card component
 */

import { motion } from 'framer-motion';
import { cn } from '@/utils/cn';
import { useSubscriptionStore } from '@/stores';
import type { TierConfig } from '@/types/subscription';
import { FEATURE_NAMES } from '../config';

interface PricingCardProps {
  tier: TierConfig;
  isCurrentTier?: boolean;
  onSelect: () => void;
  billingPeriod: 'monthly' | 'annual';
}

export function PricingCard({
  tier,
  isCurrentTier,
  onSelect,
  billingPeriod,
}: PricingCardProps) {
  const getLocalizedPrice = useSubscriptionStore((state) => state.getLocalizedPrice);

  const price = billingPeriod === 'annual' && tier.priceAnnualUSD
    ? getLocalizedPrice(tier.priceAnnualUSD / 12)
    : getLocalizedPrice(tier.priceUSD);

  const features = Object.entries(tier.features)
    .filter(([key, value]) => {
      if (typeof value === 'boolean') return value;
      if (key === 'apiCallsPerMonth') return true;
      return true;
    })
    .map(([key, value]) => {
      const name = FEATURE_NAMES[key as keyof typeof FEATURE_NAMES] || key;
      let displayValue = '';

      if (key === 'apiCallsPerMonth') {
        displayValue = `${(value as number).toLocaleString()} API calls/month`;
      } else if (key === 'forecast') {
        displayValue = `${value} Forecast`;
      } else if (key === 'mapAccess') {
        displayValue = value === 'full' ? 'Full Map Access' : 'Basic Map Access';
      }

      return { name, value, displayValue };
    });

  return (
    <motion.div
      whileHover={{ scale: 1.02, y: -4 }}
      className={cn(
        'relative flex flex-col p-5 sm:p-6 rounded-2xl',
        'bg-white/[0.03] backdrop-blur-xl border',
        'transition-all duration-300',
        tier.highlighted
          ? 'border-hud-primary/60 shadow-lg shadow-hud-primary/10'
          : 'border-white/[0.08] hover:border-white/[0.15]',
        isCurrentTier && 'ring-2 ring-emerald-500/60'
      )}
    >
      {/* Badge */}
      {tier.badge && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className={cn(
            'px-3 py-1 rounded-full text-[11px] font-semibold whitespace-nowrap',
            tier.highlighted
              ? 'bg-hud-primary text-white'
              : 'bg-white/10 text-white/80 border border-white/10'
          )}>
            {tier.badge}
          </span>
        </div>
      )}

      {/* Current tier indicator */}
      {isCurrentTier && (
        <div className="absolute -top-3 right-4">
          <span className="px-3 py-1 rounded-full text-[11px] font-semibold bg-emerald-500 text-white">
            Current
          </span>
        </div>
      )}

      {/* Header */}
      <div className="mb-5">
        <h3 className="text-lg font-bold text-white mb-0.5">{tier.name}</h3>
        <p className="text-xs text-text-secondary">{tier.description}</p>
      </div>

      {/* Price */}
      <div className="mb-5">
        {tier.priceUSD === 0 ? (
          <div className="flex items-baseline">
            <span className="text-3xl font-bold text-white">Free</span>
          </div>
        ) : (
          <div>
            <div className="flex items-baseline gap-1">
              <span className="text-3xl font-bold text-white">{price.formatted}</span>
              <span className="text-sm text-text-secondary">/mo</span>
            </div>
            {price.currency !== 'USD' && (
              <p className="text-xs text-text-muted mt-1">
                ~${tier.priceUSD} USD
              </p>
            )}
            {billingPeriod === 'annual' && tier.priceAnnualUSD && (
              <p className="text-xs text-emerald-400 mt-1">
                Save {Math.round((1 - tier.priceAnnualUSD / (tier.priceUSD * 12)) * 100)}% annually
              </p>
            )}
          </div>
        )}
      </div>

      {/* Features */}
      <ul className="flex-1 space-y-2.5 mb-5">
        {features.map(({ name, value, displayValue }) => (
          <li key={name} className="flex items-start gap-2">
            <svg
              className={cn(
                'w-4 h-4 flex-shrink-0 mt-0.5',
                value ? 'text-emerald-400' : 'text-white/20'
              )}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              {value ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              )}
            </svg>
            <span className={cn(
              'text-[13px]',
              value ? 'text-text-primary' : 'text-white/25 line-through'
            )}>
              {displayValue || name}
            </span>
          </li>
        ))}
      </ul>

      {/* CTA Button */}
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={onSelect}
        disabled={isCurrentTier}
        className={cn(
          'w-full py-2.5 sm:py-3 rounded-xl text-sm font-semibold transition-all duration-200',
          tier.highlighted
            ? 'bg-gradient-to-r from-hud-primary to-teal-600 text-white hover:shadow-lg hover:shadow-hud-primary/20'
            : tier.priceUSD === 0
            ? 'bg-white/[0.04] border border-white/[0.08] text-text-primary hover:bg-white/[0.08]'
            : 'bg-white/[0.04] border border-white/[0.1] text-white hover:bg-white/[0.08]',
          isCurrentTier && 'opacity-40 cursor-not-allowed'
        )}
      >
        {isCurrentTier
          ? 'Current Plan'
          : tier.priceUSD === 0
          ? 'Get Started'
          : 'Start Free Trial'}
      </motion.button>
    </motion.div>
  );
}
